<template>
  <dt-stack
    :class="emptyStateClasses"
  >
    <span
      v-if="showIllustration"
      class="d-empty-state__illustration"
    >
      <!-- @slot Slot for the illustration. Displays when size is 'lg' or 'md'. Overrides icon. -->
      <slot name="illustration" />
    </span>

    <span
      v-if="showIcon"
      class="d-empty-state__icon"
    >
      <!-- @slot Slot for the icon. Displayed if illustration is not provided. -->
      <slot
        name="icon"
        :icon-size="'800'"
      />
    </span>

    <dt-stack
      gap="450"
      :class="['d-empty-state__content', contentClass]"
    >
      <div :class="['d-empty-state__header-text', headlineClass]">
        {{ headerText }}
      </div>

      <p
        v-if="bodyText"
        :class="['d-empty-state__body-text', bodyClass]"
      >
        {{ bodyText }}
      </p>
    </dt-stack>

    <slot name="body" />
  </dt-stack>
</template>

<script setup>
import { useSlots, computed, onMounted } from 'vue';
import { DtStack } from '@/components/stack';
import { hasSlotContent } from '@/common/utils';
import {
  EMPTY_STATE_BODY_SIZE_MODIFIERS,
  EMPTY_STATE_CONTENT_SIZE_MODIFIERS,
  EMPTY_STATE_HEADLINE_SIZE_MODIFIERS,
  EMPTY_STATE_SIZE_MODIFIERS,
} from './empty_state_constants.js';

const slots = useSlots();

const props = defineProps({
  /**
    * The empty state size.
    * @values 'sm', 'md', 'lg'
    */
  size: {
    type: String,
    default: 'lg',
    validator: (s) => Object.keys(EMPTY_STATE_SIZE_MODIFIERS).includes(s),
  },

  /**
    * Header text
    * @type {String}
    */
  headerText: {
    type: String,
    required: true,
  },

  /**
    * Body text
    * @type {String}
    */
  bodyText: {
    type: String,
    default: null,
  },
});

const hasIcon = computed(() => {
  return hasSlotContent(slots.icon);
});
const hasIllustration = computed(() => hasSlotContent(slots.illustration));
const isSmallSize = computed(() => props.size === 'sm');

/**
 * Icon will be shown in lg and md size only if illustration is not provided
 * Icon will always be shown in sm size
 */
const showIcon = computed(() => hasIcon.value && (!hasIllustration.value || isSmallSize.value));

/**
 * Illustration will always be shown in lg and md size
 * Illustration will not be shown in sm size
 */
const showIllustration = computed(() => hasIllustration.value && !isSmallSize.value);

const sizeClass = computed(() => EMPTY_STATE_SIZE_MODIFIERS[props.size]);

const emptyStateClasses = computed(() => ['d-empty-state', sizeClass.value]);

const contentClass = computed(() => EMPTY_STATE_CONTENT_SIZE_MODIFIERS[props.size]);

const headlineClass = computed(() => EMPTY_STATE_HEADLINE_SIZE_MODIFIERS[props.size]);

const bodyClass = computed(() => EMPTY_STATE_BODY_SIZE_MODIFIERS[props.size]);

onMounted(() => {
  if (!props.bodyText && !hasSlotContent(slots.body)) {
    console.error('DtEmptyState: You should provide either bodyText or content on body slot.');
  }
});
</script>
